import geometry from '../../../../scene/util/geometry';
import {
  isType,
  isSection,
  getFittingShelf,
} from '../../../../services/products';
import { getBoundedItem } from '../../../../services/products/models';
import constants from '../../../../settings/constants';
import { unique } from '../../../../util/array';
import tacHelpers from '../../tacHelpers';
import { getDependencyDiff } from './dependentItems';
import { ITEMS } from '../../../../constants';
import productService from '../../../../services/products';

const { SHELF_DRAWER, BOX, DOOR, SHELF, DRAWER, CABINET, CHEST, SECTION } =
  ITEMS;

/**
 * A function which will be called when dropping an ivar component in the planner
 *
 * @param {Array} slots An array of sots describing the holes available to hang the component on it
 * @returns {Boolean}
 */
function filterSlots(slots, tac) {
  const SHELF_DRAWER_MAX_Y = 1400;
  const DRAWER_MAX_Y_PERCENTAGE_OF_SECTION_HEIGHT = 0.51;
  const HIGH_CABINET_HEIGHT = 1600;
  const SHORT_SECTION_HEIGHT = 1240;
  const MESH_CABINETS_HEIGHT = 830;
  const SLOT_Y_LIMIT = 121;
  const SLOT_Y_LIMIT_MESH_CABINET_SHORT_SECTION = 377;
  return slots.filter(slot => {
    const collisionItem = getBoundedItem(slot, 'collision');
    const extendsAbove =
      collisionItem.y + collisionItem.height >
      slot.parent.y + slot.parent.height;
    if (
      isType(slot, CABINET) &&
      slot.height === MESH_CABINETS_HEIGHT &&
      slot.parent.height === SHORT_SECTION_HEIGHT
    ) {
      return slot.y <= SLOT_Y_LIMIT_MESH_CABINET_SHORT_SECTION;
    }
    if (
      isType(slot, CHEST) ||
      (isType(slot, CABINET) && slot.height === HIGH_CABINET_HEIGHT)
    ) {
      return slot.y <= SLOT_Y_LIMIT && !extendsAbove;
    }
    if (isType(slot, SHELF_DRAWER)) {
      const grandParent = tacHelpers.getParent(tac, slot.parent);
      return (
        slot.y + slot.height <= SHELF_DRAWER_MAX_Y &&
        (!isSection(grandParent) ||
          slot.parent.y + constants.DISTANCE_BETWEEN_ATTACHMENTS <
            grandParent.y + grandParent.height)
      );
    }
    if (isType(slot, DRAWER)) {
      return (
        slot.parent.height * DRAWER_MAX_Y_PERCENTAGE_OF_SECTION_HEIGHT > slot.y
      );
    }
    if (isType(slot, SHELF) && slot.items?.length) {
      return (
        slot.width >=
          slot.items.reduce((totalWidth, item) => totalWidth + item.width, 0) &&
        slot.y <= SHELF_DRAWER_MAX_Y &&
        slot.y + constants.DISTANCE_BETWEEN_ATTACHMENTS <
          slot.parent.y + slot.parent.height
      );
    }
    if (isType(slot, DOOR)) {
      return (
        !!getFittingShelf(slot.parent) &&
        slot.y + slot.height <= slot.parent.y + slot.parent.height
      );
    }

    return !extendsAbove;
  });
}

function findSuperSection(tac, member, skiplist) {
  if (!tacHelpers.isWithinWall(member, tac)) {
    return [member];
  }

  const superSection = [member];

  const candidates = tac.items.filter(
    item =>
      item.itemid !== member.itemid &&
      skiplist.indexOf(item.itemid) === -1 &&
      tacHelpers.isWithinWall(item, tac)
  );

  const fullSizeMember = tacHelpers.getFullSize(member);

  const collisions = candidates.filter(candidate =>
    geometry.collides(fullSizeMember, tacHelpers.getFullSize(candidate))
  );
  const skip = skiplist
    .concat(collisions.map(item => item.itemid))
    .filter(unique);

  const rest = collisions.map(collision =>
    findSuperSection(tac, collision, skip)
  );

  return Array.prototype.concat.apply(superSection, rest).filter(unique);
}

/**
 * Checks whether a section has an obstructing item placed
 * above the space in-between its posts
 *
 * @param {Object} section The section to check if blocked
 * @param {Object} overlapper The other section asking to overlap
 * @param {Array} nonSections All top-level items in the TAC that are not sections
 * @param {Object} tac The entire TAC
 * @returns {Boolean} If there is a blocking item or not
 */
function isBlockedAboveCenter(section, overlapper, nonSections, tac) {
  const postWidth = productService.getPostWidth();
  const spaceBetweenAfterMerge = {
    ...section,
    x: section.x + postWidth,
    z: 0,
    width: section.width - postWidth * 2,
    height: Math.max(overlapper.height, section.height),
  };

  if (
    nonSections.some(item =>
      geometry.contains(spaceBetweenAfterMerge, { ...item, height: 1 })
    )
  ) {
    // Something is placed inside the area above the relative
    const superSection = findSuperSection(tac, section, [overlapper.itemid]);
    if (
      superSection.find(
        item =>
          isSection(item) &&
          item.height > section.height &&
          (item.x < section.x || item.x > section.x)
      )
    ) {
      // Our relative has a higher neighbour on at least one side,
      // meaning that the area above would become a section area post-merge
      return true;
    }
  }
}

/**
 * Checks the conditions for potential merge on respective side of the relative
 *
 * @param {Object} item The section asking for what overlap is allowed
 * @param {Object} relative The other section we might want to overlap
 * @param {Object} tac The entire TAC
 * @returns {Object} An object with boolean properties "left" and "right" stating whether overlap is allowed
 */
function getBlockedDirections(item, relative, tac) {
  const postWidth = productService.getPostWidth();
  const blockedDirections = {
    left: false,
    right: false,
  };

  const nonSections = tac.items.filter(item => !isSection(item));

  if (isBlockedAboveCenter(relative, item, nonSections, tac)) {
    blockedDirections.left = true;
    blockedDirections.right = true;
  }

  // Either side could also be blocked if there is something placed above the actual post
  if (!blockedDirections.left) {
    const leftPostAfterMerge = {
      ...relative,
      width: postWidth,
      height: Math.max(item.height, relative.height),
    };

    const itemAfterMergeLeft = {
      ...item,
      x: leftPostAfterMerge.x - item.width,
      y: leftPostAfterMerge.y,
      z: leftPostAfterMerge.z,
    };

    blockedDirections.left =
      geometry.getCollidingRects(leftPostAfterMerge, nonSections).length ||
      isBlockedAboveCenter(itemAfterMergeLeft, relative, nonSections, tac);
  }

  if (!blockedDirections.right) {
    const rightPostAfterMerge = {
      ...relative,
      x: relative.x + relative.width - postWidth,
      width: postWidth,
      height: Math.max(item.height, relative.height),
    };

    const itemAfterMergeRight = {
      ...item,
      x: rightPostAfterMerge.x,
      y: rightPostAfterMerge.y,
      z: rightPostAfterMerge.z,
    };

    blockedDirections.right =
      geometry.getCollidingRects(rightPostAfterMerge, nonSections).length ||
      isBlockedAboveCenter(itemAfterMergeRight, relative, nonSections, tac);
  }

  return blockedDirections;
}

/**
 * Get the allowed overlap for an item to on another item (the relative)
 *
 * @param {Object} tac The entire TAC
 * @param {Object} item The item asking for what overlap is allowed
 * @param {Object} relative The other item we might want to overlap
 * @returns {Object} An object with the dimensions of the allowed overlap
 */
function getAllowedOverlap(tac, item, relative) {
  let x = 0;
  let width = 0;

  const postWidth = productService.getPostWidth();
  const sameDepthSectionRelation =
    item.depth === relative.depth && [item, relative].every(isSection);

  if (sameDepthSectionRelation) {
    const blockedDirections = getBlockedDirections(item, relative, tac);

    x = !blockedDirections.left ? postWidth : 0;
    width =
      -postWidth *
      (blockedDirections.left && blockedDirections.right
        ? 0
        : !blockedDirections.left && !blockedDirections.right
        ? 2
        : 1);
  }

  return { x, y: 0, z: 0, width, height: 0, depth: 0 };
}

/**
 * Get the "snapped" position of a dragged item, if close enough to a suitable partner
 *
 * @param {Object} item The item asking to snap
 * @param {Array} rects All other rects on the scene
 * @returns {Object | undefined} An object with new coordinates for the snapped position,
 * or undefined if no suitable partner found
 */
function getSnappingPosition(item, rects) {
  if (!isSection(item)) {
    return;
  }

  const postWidth = productService.getPostWidth();
  const sectionRects = rects.filter(isSection);
  const closeRects = geometry.getCollidingRects(item, sectionRects, {
    right: constants.SECTION_SNAPPING_DISTANCE + postWidth - 1,
    left: constants.SECTION_SNAPPING_DISTANCE + postWidth - 1,
  });

  const partner = geometry.closestCollidingRect(item, closeRects);

  if (!partner?.overlap || partner.depth !== item.depth) {
    return;
  }

  const { overlap } = partner;

  if (partner.x < item.x && overlap.width + overlap.x < 0) {
    return { x: partner.x + partner.width };
  } else if (partner.x > item.x && overlap.x > 0) {
    return { x: partner.x - item.width };
  }
}

/**
 * Checks whether an incoming item is allowed to ignore the spacing bounds of
 * an already present item.
 *
 * @param {Object} candidate The item asking to ignore its spacing bounds
 * @param {Object} relative The other item in the relation
 * @param {Object} [tac] The entire TAC
 * @returns {Boolean} True/false depending on if ignoring is allowed,
 */
function shouldIgnoreSpacingBounds(candidate, relative = null, tac = null) {
  if (
    [candidate, relative].every(
      item => item && (item.belongsTo || isType(item, DOOR))
    ) ||
    [candidate, relative].every(item => item && isType(item, CABINET))
  ) {
    // Never enforce spacing bounds between doors or cabinets
    return true;
  }

  const relativeIsStorageBox = relative && isType(relative, BOX);

  const candidateHasDrawers =
    isType(candidate, SHELF) &&
    candidate.items?.length &&
    candidate.items.some(item => isType(item, SHELF_DRAWER));

  if (relativeIsStorageBox) {
    // A storage box never cares about spacing bounds,
    // unless it sits below a shelf with drawer
    return !candidateHasDrawers;
  }

  const someoneIsStandAloneCabinet =
    [candidate, relative]
      .filter(Boolean)
      .some(item => isType(item, CABINET) && item.z < 0) ||
    (tac && isType(tacHelpers.getTopAncestor(tac, candidate), CABINET));

  if (someoneIsStandAloneCabinet) {
    // Cabinet spacing bounds only apply when placed inside a section and towards
    // other stuff in that same section
    return true;
  }

  if (relative) {
    const relativeIsShelfDrawer = isType(relative, SHELF_DRAWER);
    const relativeHasShelfDrawer =
      isType(relative, SHELF) &&
      relative.items?.length &&
      relative.items.some(item => isType(item, SHELF_DRAWER));

    const isShelfDrawerAboveSiblingRelation =
      (relativeIsShelfDrawer || relativeHasShelfDrawer) &&
      relative.y > candidate.y + candidate.height;

    if (isShelfDrawerAboveSiblingRelation) {
      // Spacing bounds doesn't apply to shelf drawers above
      return true;
    }

    const isShelfOrDoorAboveDrawerRelation =
      isType(candidate, [SHELF, DOOR]) &&
      !candidate.items?.length &&
      isType(relative, DRAWER) &&
      candidate.y > relative.y + relative.height;

    if (isShelfOrDoorAboveDrawerRelation) {
      // Shelf spacing bounds doesn't apply to drawers below it
      return true;
    }
  }

  return false;
}

function shouldIgnoreCollision(item, parent, relative) {
  return (
    isType(parent, SECTION) &&
    isType(relative, SECTION) &&
    geometry.collides(parent, relative)
  );
}

/**
 * Gets all the other items in the TAC that are dependent of a specific item
 * @param {*} item
 * @param {*} tac
 * @returns {Array}
 */
const getDependentItems = (item, tac) => {
  if (isType(item, DOOR)) {
    const allItems = tacHelpers.getAllItems(tac.items);
    return allItems.filter(
      ({ belongsTo }) => belongsTo && belongsTo === item.itemid
    );
  }
  return [];
};

export default {
  filterSlots,
  findSuperSection,
  getAllowedOverlap,
  getDependencyDiff,
  getSnappingPosition,
  shouldIgnoreCollision,
  shouldIgnoreSpacingBounds,
  getDependentItems,
};
